import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import CharacterDetailModal from '../components/CharacterDetailModal';
import Image from '../components/Image';
import { loadCharacterData } from '../lib/characterData';
import { readJson, writeJson } from '../lib/storage';

function normalizeParty(party) {
  return {
    name: party?.name || '',
    members: Array.isArray(party?.members) ? party.members : [],
    memo: party?.memo || '',
  };
}

export default function PartyManagementPage() {
  const [characters, setCharacters] = useState([]);
  const [parties, setParties] = useState([normalizeParty(null)]);
  const [confirmIndex, setConfirmIndex] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [autocompleteInputs, setAutocompleteInputs] = useState({});

  useEffect(() => {
    let active = true;

    loadCharacterData().then((data) => {
      if (active) {
        setCharacters(data);
      }
    });

    const stored = readJson('parties', null);
    if (stored && Array.isArray(stored) && stored.length > 0) {
      setParties(stored.map(normalizeParty));
    }

    return () => {
      active = false;
    };
  }, []);

  const characterMap = useMemo(() => {
    const map = new Map();
    characters.forEach((c) => map.set(String(c.id), c));
    return map;
  }, [characters]);

  const updateParties = (next) => {
    setParties(next);
    writeJson('parties', next);
  };

  const updateParty = (index, updater) => {
    const next = parties.map((party, i) => (i === index ? updater(party) : party));
    updateParties(next);
  };

  const addParty = () => {
    updateParties([...parties, normalizeParty(null)]);
  };

  const removeParty = () => {
    if (confirmIndex == null) {
      return;
    }
    const next = parties.filter((_, i) => i !== confirmIndex);
    updateParties(next.length > 0 ? next : [normalizeParty(null)]);
    setConfirmIndex(null);
  };

  const addMember = (partyIndex, character) => {
    updateParty(partyIndex, (party) => {
      if (party.members.length >= 5) {
        return party;
      }
      if (party.members.some((m) => String(m.id) === String(character.id))) {
        return party;
      }
      return { ...party, members: [...party.members, character] };
    });
    setAutocompleteInputs((prev) => ({ ...prev, [partyIndex]: '' }));
  };

  const removeMember = (partyIndex, characterId) => {
    updateParty(partyIndex, (party) => ({
      ...party,
      members: party.members.filter((m) => String(m.id) !== String(characterId)),
    }));
  };

  return (
    <Container sx={{ py: 2 }}>
      {parties.map((party, index) => {
        const memberIds = new Set(party.members.map((m) => String(m.id)));

        return (
          <Box key={`party-${index}`} sx={{ border: '1px solid', borderColor: 'divider', p: 3, borderRadius: 2, mb: 4, backgroundColor: 'background.paper', boxShadow: 1 }}>
            <TextField
              fullWidth
              label="パーティ名"
              value={party.name}
              onChange={(e) => updateParty(index, (p) => ({ ...p, name: e.target.value }))}
            />

            <Grid container spacing={1} sx={{ mt: 1 }}>
              {party.members.map((member) => (
                <Grid item key={`${index}-${member.id}`} xs={3} sm={2} md={1.5} lg={1}>
                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'rgba(30, 41, 59, 0.5)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <Image
                        src={`/img/chara/${member.id}.png`}
                        alt={member.name}
                        style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block' }}
                      />
                      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '35%', background: 'linear-gradient(to top, rgba(15,23,42,0.9), transparent)', pointerEvents: 'none' }} />
                      <IconButton
                        size="small"
                        onClick={() => setSelectedCharacter(characterMap.get(String(member.id)) || member)}
                        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }, width: 26, height: 26 }}
                      >
                        <ArticleIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Box>
                    <Box sx={{ bgcolor: 'rgba(15, 23, 42, 0.95)' }}>
                      <Button fullWidth color="error" size="small" sx={{ borderRadius: 0, fontSize: '0.75rem', py: 0.8 }} onClick={() => removeMember(index, member.id)}>
                        外す
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              ))}

              {party.members.length < 5 ? (
                <Grid item xs={12} sm={8} md={4}>
                  <Autocomplete
                    options={characters.filter((c) => !memberIds.has(String(c.id)))}
                    getOptionLabel={(option) => `[${option.title}] ${option.name}`}
                    value={null}
                    inputValue={autocompleteInputs[index] || ''}
                    onInputChange={(_event, value) =>
                      setAutocompleteInputs((prev) => ({ ...prev, [index]: value }))
                    }
                    onChange={(_event, value) => value && addMember(index, value)}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Avatar sx={{ width: 28, height: 28, mr: 1 }} src={`/img/chara/${option.id}.png`} />
                        {`[${option.title}] ${option.name}`}
                      </Box>
                    )}
                    renderInput={(params) => <TextField {...params} label="メンバー追加" />}
                  />
                </Grid>
              ) : null}
            </Grid>

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="メモ"
              sx={{ mt: 2 }}
              value={party.memo}
              onChange={(e) => updateParty(index, (p) => ({ ...p, memo: e.target.value }))}
            />

            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button variant="outlined" color="error" onClick={() => setConfirmIndex(index)}>
                パーティ削除
              </Button>
            </Box>
          </Box>
        );
      })}

      <Button variant="contained" onClick={addParty}>
        パーティ追加
      </Button>

      <Dialog open={confirmIndex != null} onClose={() => setConfirmIndex(null)}>
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <DialogContentText>このパーティを削除しますか？</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmIndex(null)}>キャンセル</Button>
          <Button color="error" variant="contained" onClick={removeParty}>
            削除
          </Button>
        </DialogActions>
      </Dialog>

      <CharacterDetailModal
        open={Boolean(selectedCharacter)}
        character={selectedCharacter}
        onClose={() => setSelectedCharacter(null)}
      />
    </Container>
  );
}
